import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { InitialInterviewApiService } from "../initial-interview-api.service";
import { CustomQuestion, InitialInterviewQuestion } from "../../../../typings";
import { QuestionService } from "../service/question-service";
import { ActivityType, InitialInterviewPage, InitialInterviewQuestionType, QuestionType } from "../../../enums";
import { Subscription, interval } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import { PreInterviewQuestionsService } from "../../../pre-interview-questions/pre-interview-questions.service";
import { MatRadioButton } from "@angular/material/radio";
import { FormBuilder, FormGroup } from "@angular/forms";
import { TalentHappyPathDataService } from "../../shared/service/talent-happy-path-data-service";
import { PageService } from "../service/page-service";
import { initialInterviewClickIllDoThisLater, routeGatherToProfileOrThankYou } from "../util";
import { Gather } from "../../../gather/gather-typings";
import { TalentProfileApiService } from '../../talent-profile/talent-profile-api.service';
import { VideoInterviewFlowService } from "../video-interview/service/video-interview-flow.service";

@Component({
  selector: 'app-initial-interview-questions',
  standalone: false,
  templateUrl: './initial-interview-questions.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './initial-interview-questions.component.scss'
})
export class InitialInterviewQuestionsComponent implements OnInit, OnChanges, OnDestroy{

  @ViewChild('questionInput') questionInput: ElementRef;
  @ViewChild('questionRadioYes') questionRadioYes: MatRadioButton;
  @ViewChild('questionRadioNo') questionRadioNo: MatRadioButton;
  jobPostingId: number;
  jobTitle: string;
  talent: Talent;
  orderId: number;
  @Input() customQuestions: CustomQuestion[];
  @Input() initialInterviewQuestions: InitialInterviewQuestion[];
  @Input() questionsLoading: boolean;
  @Input() returningCandidate: boolean;
  currentQuestionIndex = 0;
  showSubmitButton = false;
  showBackButton = false;
  sub: Subscription;
  QuestionType = QuestionType;
  questionForm: FormGroup;
  gather: Gather;
  gatherUUID: string;
  isGather: boolean;
  thankYouPath: string = '/thank-you';

  constructor(
    private initialInterviewApi: InitialInterviewApiService,
    private questionService: QuestionService,
    private router: Router,
    private preInterviewQuestionsService: PreInterviewQuestionsService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private talentHappyPathDataService: TalentHappyPathDataService,
    private pageService: PageService,
    private talentProfileApiService: TalentProfileApiService,
    private videoInterviewFlowService: VideoInterviewFlowService
  ) { }

  ngOnInit(): void {
    this.talent = this.talentHappyPathDataService.talent;
    this.jobPostingId = this.talentHappyPathDataService.postingId;
    this.jobTitle = this.talentHappyPathDataService.postingInfo.jobTitle;
    this.orderId = this.talentHappyPathDataService.postingInfo.orderId;
    this.gather = this.talentHappyPathDataService.gather;
    this.gatherUUID = this.route.snapshot.queryParams['gatheruuid'];
    this.isGather = this.gatherUUID != undefined;

    this.sub = interval(20000).subscribe(() => {
      this.saveAnswers('autoSave')
    });
    this.initializeForm();
    this.currentQuestionIndex = 0;
    if (this.returningCandidate && this.initialInterviewQuestions.length > 0) {
      this.initialInterviewApi.createTalentProfileActivity(
        ActivityType.INITIAL_INTERVIEW_QUESTIONS_STARTED, this.talent.personId, this.orderId, this.jobPostingId
      );
    }
    // Emits immediately, which is how the page resumes on the right question after waiting for the AI
    // questions. That resume point varies, so anything index-dependent is worked out here.
    this.sub.add(this.questionService.currentQuestions$.subscribe(questionNumber => {
      this.currentQuestionIndex = questionNumber;
      if (questionNumber > 0) {
        this.showBackButton = true;
        this.updateFormControl();
      }
      this.showSubmitButton = !this.questionsLoading && this.isOnLastQuestion();
    }));
  }

  /**
   * questionsLoading flips to false when generation finishes, which can leave the talent on the last
   * question with no other trigger to re-evaluate the button.
   */
  ngOnChanges(): void {
    if (this.initialInterviewQuestions) {
      this.showSubmitButton = !this.questionsLoading && this.isOnLastQuestion();
    }
  }

  /** Nothing loaded to advance to. More may still be generating; questionsLoading covers that. */
  isOnLastQuestion(): boolean {
    return this.currentQuestionIndex + 1 === this.initialInterviewQuestions.length;
  }

  initializeForm() {
    this.questionForm = this.fb.group({
      answer: ['']
    });

    this.updateFormControl();
  }

  updateFormControl() {
    const currentQuestion = this.initialInterviewQuestions[this.currentQuestionIndex];
    if (currentQuestion) {
      const currentAnswer = currentQuestion.answer || '';
      this.questionForm.get('answer').setValue(currentAnswer);
      this.questionForm.get('answer').updateValueAndValidity();
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  previousQuestion() {
    this.saveAnswers('back');
  }

  nextQuestion() {
    this.saveAnswers('next');
  }

  submitAnswers() {
    this.saveAnswers('submit');
  }

  doThisLater() {
    if (this.isGather) {
      initialInterviewClickIllDoThisLater('', this.talent, this.jobPostingId, this.orderId, this.jobTitle, this.initialInterviewApi, this.talentHappyPathDataService, this.router, this.gatherUUID);
    } else {
      initialInterviewClickIllDoThisLater('', this.talent, this.jobPostingId, this.orderId, this.jobTitle, this.initialInterviewApi, this.talentHappyPathDataService, this.router);
    }
    // Leave on the click, not on the save round trip (BIZ-41084). The answer save is best-effort,
    // exactly like the 20-second autosave; every other "later" button in this module also leaves
    // synchronously.
    this.saveAnswers('autoSave');
    this.navigateQuestions('later');
  }

  saveAnswers(action: string) {
    const hasCustomQuestions = this.initialInterviewQuestions.some(question => question.initialInterviewQuestionType === InitialInterviewQuestionType.CUSTOM_QUESTION);
    const hasPreinterviewQuestions = this.initialInterviewQuestions.some(question => question.initialInterviewQuestionType === InitialInterviewQuestionType.PRE_INTERVIEW_QUESTION);

    let answer = this.questionForm.get('answer').value;
    if (hasCustomQuestions && this.initialInterviewQuestions[this.currentQuestionIndex].initialInterviewQuestionType === InitialInterviewQuestionType.CUSTOM_QUESTION) {
      this.initialInterviewApi.saveCustomQuestionAnswer(this.initialInterviewQuestions[this.currentQuestionIndex].questionId, answer).subscribe(result => {
        this.navigateQuestions(action);
      });
    } else if (hasPreinterviewQuestions) {
      this.preInterviewQuestionsService.savePreInterviewAnswer(this.jobPostingId, this.talent.personId, this.initialInterviewQuestions[this.currentQuestionIndex].questionId, answer).subscribe( result => {
        this.navigateQuestions(action);
      });
    }
  }

  getCompletionPercentage() {
    if (this.questionsLoading) {
      return 50;
    }
    return ((this.currentQuestionIndex + 1) / this.initialInterviewQuestions.length) * 100;
  }

  navigateQuestions(action: string) {
    switch (action) {
      case 'next':
        this.setInitialInterviewQuestionAnswer();
        if (this.questionsLoading) {
          // Only wait when nothing is left, so the talent works through the whole placeholder slot
          // while the AI questions generate.
          if (this.isOnLastQuestion()) {
            this.pageService.setActivePage(InitialInterviewPage.HANG_TIGHT_PAGE);
            break;
          }
        } else if (this.initialInterviewQuestions.every(question => question.isStandardQuestion)
                   && this.isOnLastQuestion()) {
          // No AI questions arrived, so there is nothing after the placeholder slot to move on to.
          this.pageService.setActivePage(InitialInterviewPage.NO_QUESTIONS_PAGE);
          break;
        }
        this.currentQuestionIndex += 1;
        this.updateFormControl();
        this.showBackButton = true;
        this.questionService.setCurrentQuestionNumber(this.currentQuestionIndex);
        this.showSubmitButton = !this.questionsLoading && this.isOnLastQuestion();
        break;
      case 'back':
        this.setInitialInterviewQuestionAnswer();
        this.currentQuestionIndex -= 1;
        this.updateFormControl();
        this.questionService.setCurrentQuestionNumber(this.currentQuestionIndex);
        if (this.currentQuestionIndex === 0) {
          this.showBackButton = false
        }
        this.showSubmitButton = false;
        break;
      case 'submit':
        const customQuestionIds = this.initialInterviewQuestions
          .filter(question => question.initialInterviewQuestionType === InitialInterviewQuestionType.CUSTOM_QUESTION)
          .map(question => question.questionId);
        const preInterviewQuestionIds = this.initialInterviewQuestions
          .filter(question => question.initialInterviewQuestionType === InitialInterviewQuestionType.PRE_INTERVIEW_QUESTION)
          .map(question => question.questionId);
        if (customQuestionIds.length > 0) {
          this.initialInterviewApi.submitCustomQuestionAnswers(customQuestionIds, this.orderId, this.talentHappyPathDataService.talent.personId).subscribe(result => {
            if (preInterviewQuestionIds.length > 0) {
              this.preInterviewQuestionsService.submitPreInterviewAnswers(preInterviewQuestionIds, this.jobPostingId, this.talent.personId).subscribe(result => {
                this.createTalentSubmittedQuestionsActivity();
              });
            } else {
              this.createTalentSubmittedQuestionsActivity();
            }
          });
        } else if (preInterviewQuestionIds.length > 0) {
          this.preInterviewQuestionsService.submitPreInterviewAnswers(preInterviewQuestionIds, this.jobPostingId, this.talent.personId).subscribe(() => {
            this.createTalentSubmittedQuestionsActivity();
          });
        }
        break;
      case 'later':
        if (this.videoInterviewFlowService.shouldShowForWorkflowA()) {
          // Workflow A: show the video interview before leaving, even when skipping the questions.
          this.videoInterviewFlowService.enterVideoInterview();
          break;
        }
        if (this.isGather) {
          routeGatherToProfileOrThankYou(this.gather.talentPersonId, this.talentProfileApiService, this.router);
        } else {
          this.router.navigateByUrl('/talent-profile', { skipLocationChange: true });
        }
        break;
      case 'autoSave':
        break;
    }
  }

  createTalentSubmittedQuestionsActivity() {
    this.initialInterviewApi.createTalentSubmittedQuestionsActivity(this.talent.personId, this.orderId, this.jobPostingId).subscribe(() => {
      if (this.videoInterviewFlowService.shouldShowForWorkflowA()) {
        // Workflow A: video interview after the IIQ questions complete.
        this.videoInterviewFlowService.enterVideoInterview();
        return;
      }
      if (this.isGather) {
        routeGatherToProfileOrThankYou(this.gather.talentPersonId, this.talentProfileApiService, this.router);
      } else {
        this.router.navigate(['/talent-profile'], { skipLocationChange: true });
      }
    });
  }

  setInitialInterviewQuestionAnswer() {
    this.initialInterviewQuestions[this.currentQuestionIndex].answer = this.questionForm.get('answer').value;
  }

  protected readonly InitialInterviewQuestionType = InitialInterviewQuestionType;
}
